import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { faceMatchActions } from 'store/faceMatchSlice/faceMatchSlice';
import { selectFaceMatchIsLoadingForTransaction } from 'store/faceMatchSlice/faceMatchSelectors';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { findCameraByType, captureSnapshotFromCamera, getSnapshotErrorMessage } from 'utils/faceMatchCameraUtils';
import { IMAGE_CATEGORIES } from '../constants';
import { getNowFormattedDate } from 'utils/getNowFormatedDate';
import { FACE_MATCH_IMAGE_SOURCE } from 'utils/faceMatchEnforcement';

const FACE_MATCH_PAIRS = {
    CAR_FACE: 'CAR_ID',
    CAR_ID: 'CAR_FACE',
    TRUCK_FACE: 'TRUCK_ID',
    TRUCK_ID: 'TRUCK_FACE',
};

const ID_CAMERA_TYPES = ['CAR_ID', 'TRUCK_ID'];

const useFaceMatching = ({ camInfo, tabId, transactionId }) => {
    const dispatch = useDispatch();
    const isLoading = useSelector(selectFaceMatchIsLoadingForTransaction(transactionId));
    const [capturedSnapshots, setCapturedSnapshots] = useState({ face: null, id: null });

    const findPairedCameraConfig = useCallback(
        (currentCameraType) => {
            const pairedType = FACE_MATCH_PAIRS[currentCameraType];
            if (!pairedType) return null;

            const camsConfig = camInfo?.transactionInfo?.camsConfig;
            return findCameraByType(camsConfig, pairedType);
        },
        [camInfo?.transactionInfo?.camsConfig]
    );

    const processSnapshots = useCallback(
        (faceSnapshot, idSnapshot, currentType, pairedType) => {
            if (!faceSnapshot || !idSnapshot) return;

            const sourceCameraType = ID_CAMERA_TYPES.includes(currentType) ? currentType : pairedType;
            const targetCameraType = ID_CAMERA_TYPES.includes(currentType) ? pairedType : currentType;

            const matchType = currentType.startsWith('CAR') ? 'CAR' : 'TRUCK';

            dispatch(
                faceMatchActions.compareFaces({
                    sourceImage: idSnapshot,
                    targetImage: faceSnapshot,
                    sourceCameraType,
                    targetCameraType,
                    transactionId,
                    matchType,
                    cameraPair: matchType,
                    imageSource: FACE_MATCH_IMAGE_SOURCE.LIVE_STREAMS,
                })
            );
        },
        [dispatch, transactionId]
    );

    const handleFaceMatch = useCallback(async () => {
        const currentCameraConfig = camInfo?.camConfig;
        const currentCameraType = currentCameraConfig?.type;

        if (!currentCameraType || !FACE_MATCH_PAIRS[currentCameraType]) return false;

        const pairedCameraConfig = findPairedCameraConfig(currentCameraType);
        if (!pairedCameraConfig) {
            const pairedType = FACE_MATCH_PAIRS[currentCameraType];
            dispatch(
                snackbarActions.enqueueSnackbar({
                    message: `Missing the ${pairedType} camera. You can still verify by manually selecting the face and ID photos.`,
                    options: { variant: 'warning' },
                })
            );
            return false;
        }

        dispatch(faceMatchActions.setLoading(true));

        try {
            const currentVideoElementId = cameraStreamManager.getVideoElementId(tabId, currentCameraConfig.id);
            const pairedVideoElementId = cameraStreamManager.getVideoElementId(tabId, pairedCameraConfig.id);

            const [currentSnapshot, pairedSnapshot] = await Promise.all([
                captureSnapshotFromCamera(currentVideoElementId),
                captureSnapshotFromCamera(pairedVideoElementId),
            ]);

            const isCurrentFace = !ID_CAMERA_TYPES.includes(currentCameraType);
            const faceSnapshot = isCurrentFace ? currentSnapshot : pairedSnapshot;
            const idSnapshot = isCurrentFace ? pairedSnapshot : currentSnapshot;

            setCapturedSnapshots({ face: faceSnapshot, id: idSnapshot });

            const faceCamera = isCurrentFace ? currentCameraConfig : pairedCameraConfig;
            const idCamera = isCurrentFace ? pairedCameraConfig : currentCameraConfig;
            const matchType = currentCameraType.startsWith('CAR') ? 'Car' : 'Truck';
            const timestamp = getNowFormattedDate();

            dispatch(
                formControlActions.saveCameraStreamSnapshot({
                    transactionId,
                    snapshot: faceSnapshot,
                    id: Date.now(),
                    imageCategory: IMAGE_CATEGORIES.FACE_MATCH_SNAPSHOT,
                    imageName: `${matchType} Face ${timestamp}`,
                    cameraName: faceCamera?.name || `${matchType} Face Camera`,
                })
            );

            dispatch(
                formControlActions.saveCameraStreamSnapshot({
                    transactionId,
                    snapshot: idSnapshot,
                    id: Date.now() + 1,
                    imageCategory: IMAGE_CATEGORIES.FACE_MATCH_SNAPSHOT,
                    imageName: `${matchType} ID ${timestamp}`,
                    cameraName: idCamera?.name || `${matchType} ID Camera`,
                })
            );

            processSnapshots(faceSnapshot, idSnapshot, currentCameraType, pairedCameraConfig.type);
            return true;
        } catch (error) {
            dispatch(faceMatchActions.setLoading(false));
            dispatch(
                faceMatchActions.compareFacesFailure({
                    transactionId,
                    error: {
                        message: error.message || 'Failed to capture snapshots',
                        code: 'SNAPSHOT_ERROR',
                    },
                })
            );
            dispatch(
                snackbarActions.enqueueSnackbar({
                    message: getSnapshotErrorMessage(error),
                    options: { variant: 'error' },
                })
            );
            return false;
        }
    }, [camInfo?.camConfig, findPairedCameraConfig, tabId, processSnapshots, dispatch, transactionId]);

    const isFaceMatchCamera = useCallback(() => {
        const cameraType = camInfo?.camConfig?.type;
        return cameraType && Object.keys(FACE_MATCH_PAIRS).includes(cameraType);
    }, [camInfo?.camConfig?.type]);

    const getPairedCamera = useCallback(() => {
        const currentCameraType = camInfo?.camConfig?.type;
        if (!currentCameraType) return null;
        return findPairedCameraConfig(currentCameraType);
    }, [camInfo?.camConfig?.type, findPairedCameraConfig]);

    return {
        handleFaceMatch,
        isFaceMatchCamera,
        isLoading,
        capturedSnapshots,
        getPairedCamera,
    };
};

export default useFaceMatching;
